/**
 * Domain Layer - ReservationStatus ValueObject
 * 診療予約ステータスの値オブジェクト定義
 */

/**
 * 診療予約ステータスの列挙型
 */
export const ReservationStatus = {
  /** 初診 */
  FIRST_VISIT: 'FIRST_VISIT',
  /** 再診（緩和薬） */
  REVISIT_PALLIATIVE: 'REVISIT_PALLIATIVE',
  /** 再診（種類変更） */
  REVISIT_TYPE_CHANGE: 'REVISIT_TYPE_CHANGE',
  /** 再診（相談） */
  REVISIT_CONSULTATION: 'REVISIT_CONSULTATION',
  /** 未分類 */
  UNCLASSIFIED: 'UNCLASSIFIED',
} as const;

export type ReservationStatus = typeof ReservationStatus[keyof typeof ReservationStatus];

/**
 * ステータスの日本語ラベルマッピング
 */
export const STATUS_LABELS: Record<ReservationStatus, string> = {
  [ReservationStatus.FIRST_VISIT]: '初診',
  [ReservationStatus.REVISIT_PALLIATIVE]: '再診(緩和薬)',
  [ReservationStatus.REVISIT_TYPE_CHANGE]: '再診(種類変更)',
  [ReservationStatus.REVISIT_CONSULTATION]: '再診(相談)',
  [ReservationStatus.UNCLASSIFIED]: '未分類',
} as const;

/**
 * 日本語ラベルからステータスへの逆引きマッピング
 */
export const LABEL_TO_STATUS: Record<string, ReservationStatus> = {
  '初診': ReservationStatus.FIRST_VISIT,
  '再診(緩和薬)': ReservationStatus.REVISIT_PALLIATIVE,
  '再診(種類変更)': ReservationStatus.REVISIT_TYPE_CHANGE,
  '再診(相談)': ReservationStatus.REVISIT_CONSULTATION,
  '未分類': ReservationStatus.UNCLASSIFIED,
} as const;

/**
 * ステータスの表示色マッピング（MUIのChip色に対応）
 */
export type StatusColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export const STATUS_COLORS: Record<ReservationStatus, StatusColor> = {
  [ReservationStatus.FIRST_VISIT]: 'primary',
  [ReservationStatus.REVISIT_PALLIATIVE]: 'warning',
  [ReservationStatus.REVISIT_TYPE_CHANGE]: 'secondary',
  [ReservationStatus.REVISIT_CONSULTATION]: 'info',
  [ReservationStatus.UNCLASSIFIED]: 'default',
} as const;

/**
 * 日本語ラベルの表示色マッピング（既存実装との互換性）
 */
export const LABEL_COLORS: Record<string, StatusColor> = {
  '初診': 'primary',
  '再診(緩和薬)': 'warning',
  '再診(種類変更)': 'secondary',
  '再診(相談)': 'info',
  '未分類': 'default',
} as const;

/**
 * ステータスの日本語ラベルを取得する
 * @param status ReservationStatus
 * @returns 日本語ラベル
 */
export const getStatusLabel = (status: ReservationStatus): string => {
  return STATUS_LABELS[status];
};

/**
 * 日本語ラベルからReservationStatusを取得する
 * @param label 日本語ラベル
 * @returns ReservationStatus（見つからない場合はUNCLASSIFIED）
 */
export const getStatusFromLabel = (label: string): ReservationStatus => {
  return LABEL_TO_STATUS[label] ?? ReservationStatus.UNCLASSIFIED;
};

/**
 * ステータスの表示色を取得する
 * @param status ReservationStatus
 * @returns MUI Chipの色
 */
export const getStatusColor = (status: ReservationStatus): StatusColor => {
  return STATUS_COLORS[status];
};

/**
 * 日本語ラベルから表示色を取得する
 * @param label 日本語ラベル
 * @returns MUI Chipの色
 */
export const getColorFromLabel = (label: string): StatusColor => {
  return LABEL_COLORS[label] ?? 'default';
};

/**
 * 有効なステータスラベルかどうかを判定する
 * @param label 文字列
 * @returns 有効なステータスラベルの場合true
 */
export const isValidStatusLabel = (label: string): boolean => {
  return label in LABEL_TO_STATUS;
};

/**
 * すべてのステータスラベルを取得する
 * @returns ステータスラベルの配列
 */
export const getAllStatusLabels = (): string[] => {
  return Object.values(STATUS_LABELS);
};
